import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Table,
  Tag,
  Typography,
  message,
  Divider,
  Spin,
  Alert,
  Card,
  Switch,
} from 'antd';
import { Content } from '../services/admin';
import { BulkCreateLicenseItemsDto, PreviewBulkLicenseItemsDto, licenseService } from '../services/licenseService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface BulkGenerationModalProps {
  visible: boolean;
  licenseId: number;
  onCancel: () => void;
  onSuccess: (newItems: any[]) => void;
  availableContents: Content[];
}

interface BulkFormData {
  contentType?: string;
  filename?: string;
  resolution?: string;
  durationMin?: number;
  durationMax?: number;
  bitRateMin?: number;
  bitRateMax?: number;
  validUntil: dayjs.Dayjs;
  maxPlayCount: number;
  useSpecificFiles: boolean;
  specificFileIds?: string[];
}

const BulkGenerationModal: React.FC<BulkGenerationModalProps> = ({
  visible,
  licenseId,
  onCancel,
  onSuccess,
  availableContents,
}) => {
  const [form] = Form.useForm();
  const [previewData, setPreviewData] = useState<PreviewBulkLicenseItemsDto | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [useSpecificFiles, setUseSpecificFiles] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (visible) {
      setPreviewData(null);
      setPreviewError(null);
      form.setFieldsValue({
        validUntil: dayjs().add(7, 'day'),
        maxPlayCount: 999999999,
        useSpecificFiles: false,
      });
    } else {
      setPreviewData(null);
      setPreviewError(null);
      form.resetFields();
    }
  }, [visible, form]);

  const handlePreview = async () => {
    try {
      const values = await form.validateFields();
      setIsPreviewing(true);
      setPreviewError(null);

      const bulkData: BulkCreateLicenseItemsDto = {
        validUntil: values.validUntil.format('YYYY-MM-DDTHH:mm:ss'),
        maxPlayCount: values.maxPlayCount,
      };

      // Add filters based on form values
      if (values.contentType) bulkData.contentType = values.contentType;
      if (values.filename) bulkData.filename = values.filename;
      if (values.resolution) bulkData.resolution = values.resolution;
      if (values.durationMin) bulkData.durationMin = values.durationMin;
      if (values.durationMax) bulkData.durationMax = values.durationMax;
      if (values.bitRateMin) bulkData.bitRateMin = values.bitRateMin;
      if (values.bitRateMax) bulkData.bitRateMax = values.bitRateMax;
      if (values.specificFileIds) bulkData.specificFileIds = values.specificFileIds;

      const preview = await licenseService.previewBulkLicenseItems(licenseId, bulkData);
      setPreviewData(preview);

      message.success(`Found ${preview.totalCount} items matching your criteria`);
    } catch (error: any) {
      console.error('Preview error:', error);
      setPreviewError(error.response?.data?.message || 'Failed to preview items');
      message.error('Failed to preview items');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleCreate = async () => {
    if (!previewData) {
      message.warning('Please preview first');
      return;
    }

    try {
      setIsCreating(true);

      const values = form.getFieldsValue();
      const bulkData: BulkCreateLicenseItemsDto = {
        validUntil: values.validUntil.format('YYYY-MM-DDTHH:mm:ss'),
        maxPlayCount: values.maxPlayCount,
      };

      // Add filters based on form values
      if (values.contentType) bulkData.contentType = values.contentType;
      if (values.filename) bulkData.filename = values.filename;
      if (values.resolution) bulkData.resolution = values.resolution;
      if (values.durationMin) bulkData.durationMin = values.durationMin;
      if (values.durationMax) bulkData.durationMax = values.durationMax;
      if (values.bitRateMin) bulkData.bitRateMin = values.bitRateMin;
      if (values.bitRateMax) bulkData.bitRateMax = values.bitRateMax;
      if (values.specificFileIds) bulkData.specificFileIds = values.specificFileIds;

      const result = await licenseService.createBulkLicenseItems(licenseId, bulkData);

      message.success(`Successfully created ${result.createdCount} license items`);
      onSuccess(result.items);
      onCancel();
    } catch (error: any) {
      console.error('Bulk creation error:', error);
      message.error(error.response?.data?.message || 'Failed to create license items');
    } finally {
      setIsCreating(false);
    }
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'filename',
      key: 'filename',
      width: 200,
      render: (text: string, record: Content) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>ID: {record.fileId}</div>
        </div>
      ),
    },
    {
      title: 'Content Type',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 120,
      render: (type: string) => (
        <Tag color={type === 'video' ? 'blue' : type === 'audio' ? 'green' : 'orange'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Resolution',
      dataIndex: 'resolution',
      key: 'resolution',
      width: 120,
      render: (resolution: string) => resolution || <span style={{ color: '#ccc' }}>N/A</span>,
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 120,
      render: (duration: number) => duration ? `${Math.floor(duration / 60)}m ${duration % 60}s` : <span style={{ color: '#ccc' }}>N/A</span>,
    },
    {
      title: 'Bit Rate',
      dataIndex: 'bitRate',
      key: 'bitRate',
      width: 120,
      render: (bitRate: number) => bitRate ? `${Math.round(bitRate / 1000)} kbps` : <span style={{ color: '#ccc' }}>N/A</span>,
    },
  ];

  const fileIdOptions = availableContents.map(content => ({
    label: `${content.filename} (${content.contentType})`,
    value: content.fileId,
  }));

  return (
    <Modal
      title="Bulk Generate License Items"
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            loading={isCreating}
            disabled={!previewData}
            onClick={handleCreate}
          >
            {previewData ? `Create ${previewData.totalCount} Items` : 'No Preview'}
          </Button>
          <Button
            type="default"
            loading={isPreviewing}
            onClick={handlePreview}
          >
            Preview
          </Button>
        </Space>
      }
    >
      <Row gutter={24}>
        {/* Left side - Filters */}
        <Col span={12}>
          <Card title="Filter Content" size="small">
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                validUntil: dayjs().add(7, 'day'),
                maxPlayCount: 999999999,
                useSpecificFiles: false,
              }}
            >
              <Form.Item
                label="Mode"
                name="useSpecificFiles"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Specific Files"
                  unCheckedChildren="Filtered Search"
                  onChange={setUseSpecificFiles}
                />
              </Form.Item>

              {!useSpecificFiles ? (
                <>
                  <Form.Item
                    label="Content Type"
                    name="contentType"
                  >
                    <Select
                      placeholder="All content types"
                      allowClear
                    >
                      <Select.Option value="video">Video</Select.Option>
                      <Select.Option value="audio">Audio</Select.Option>
                      <Select.Option value="document">Document</Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Filename Contains"
                    name="filename"
                  >
                    <Input placeholder="e.g., movie, song, document" />
                  </Form.Item>

                  <Form.Item
                    label="Resolution"
                    name="resolution"
                  >
                    <Input placeholder="e.g., 1920x1080" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Min Duration (sec)"
                        name="durationMin"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Max Duration (sec)"
                        name="durationMax"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="∞"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Min Bit Rate (bps)"
                        name="bitRateMin"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="0"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Max Bit Rate (bps)"
                        name="bitRateMax"
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          placeholder="∞"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ) : (
                <Form.Item
                  label="Select Specific Files"
                  name="specificFileIds"
                  rules={[{ required: true, message: 'Please select at least one file' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select files to license"
                    options={fileIdOptions}
                    maxTagCount={5}
                  />
                </Form.Item>
              )}

              <Divider>License Settings</Divider>

              <Form.Item
                label="Valid Until"
                name="validUntil"
                rules={[{ required: true, message: 'Please select expiration date' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                />
              </Form.Item>

              <Form.Item
                label="Max Play Count"
                name="maxPlayCount"
                rules={[{ required: true, type: 'number', min: 1 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  placeholder="Maximum play count for each file"
                />
              </Form.Item>
            </Form>

            {previewError && (
              <Alert
                message="Preview Error"
                description={previewError}
                type="error"
                style={{ marginTop: 16 }}
                closable
                onClose={() => setPreviewError(null)}
              />
            )}
          </Card>
        </Col>

        {/* Right side - Preview */}
        <Col span={12}>
          <Card title="Preview" size="small">
            {!previewData && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Text type="secondary">
                  Click "Preview" to see which files will be added
                </Text>
              </div>
            )}

            {previewData && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>
                    Found {previewData.totalCount} items matching your criteria:
                  </Text>
                </div>

                <Table
                  dataSource={previewData.contents}
                  columns={columns}
                  rowKey="fileId"
                  pagination={{
                    pageSize: 5,
                    showTotal: (total) => `${total} items`,
                  }}
                  size="small"
                  locale={{
                    emptyText: 'No content matches your criteria',
                  }}
                />

                {previewData.totalCount === 0 && (
                  <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Text type="secondary">
                      Try adjusting your filters or selecting different criteria
                    </Text>
                  </div>
                )}
              </>
            )}

            {isPreviewing && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Searching content...</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default BulkGenerationModal;